import { DateTime } from 'luxon'
import type { AppointmentDtoRecurrenceTemplate } from '~/clinician-api/models'
import type { BpEvent } from '../models'

export interface RecurringFormValue {
  recurring: string
  occurrenceCount: number
}

export const transformRecurrenceToDto = (
  formValues: RecurringFormValue
): AppointmentDtoRecurrenceTemplate | undefined => {
  if (formValues.recurring === 'no-repeat') {
    return undefined
  }

  const recurrenceMap: Record<
    string,
    {
      recurrenceType: 'd' | 'wk' | 'mo' | 'a'
      weekInterval?: number
    }
  > = {
    daily: { recurrenceType: 'd' },
    weekly: { recurrenceType: 'wk', weekInterval: 1 },
    'bi-weekly': { recurrenceType: 'wk', weekInterval: 2 },
    'third-week': { recurrenceType: 'wk', weekInterval: 3 },
    'fourth-week': { recurrenceType: 'wk', weekInterval: 4 },
    monthly: { recurrenceType: 'mo' },
    annually: { recurrenceType: 'a' }
  }

  const config = recurrenceMap[formValues.recurring]
  if (!config) {
    return undefined
  }

  const recurrenceTemplate: AppointmentDtoRecurrenceTemplate = {
    recurrenceType: config.recurrenceType,
    occurrenceCount: formValues.occurrenceCount
  }

  if (config.recurrenceType === 'wk' && config.weekInterval) {
    recurrenceTemplate.weeklyTemplate = {
      weekInterval: config.weekInterval
    }
  }

  return recurrenceTemplate
}

export const getRecurringTitle = (event?: BpEvent): string | null => {
  if (!event?.recurrenceTemplate || !event.start) {
    return null
  }

  const { recurrenceTemplate, start } = event
  const dayOfWeek = DateTime.fromISO(start).toFormat('ccc')
  const occurrenceCount = recurrenceTemplate.occurrenceCount

  let frequencyText = ''

  if (recurrenceTemplate.recurrenceType === 'd') {
    frequencyText = 'daily'
  } else if (recurrenceTemplate.recurrenceType === 'wk') {
    const weekInterval = recurrenceTemplate.weeklyTemplate?.weekInterval || 1
    if (weekInterval === 1) {
      frequencyText = 'weekly'
    } else if (weekInterval === 2) {
      frequencyText = 'every 2 weeks'
    } else {
      frequencyText = `every ${weekInterval} weeks`
    }
  } else if (recurrenceTemplate.recurrenceType === 'mo') {
    frequencyText = 'monthly'
  } else if (recurrenceTemplate.recurrenceType === 'a') {
    frequencyText = 'annually'
  }

  const timesText = occurrenceCount === 1 ? 'time' : 'times'

  if (recurrenceTemplate.recurrenceType === 'wk') {
    return `Recurring (${dayOfWeek}, ${frequencyText}, ${occurrenceCount} ${timesText})`
  }

  return `Recurring (${frequencyText}, ${occurrenceCount} ${timesText})`
}
